import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { onAuthStateChange } from '../services/auth';
import type { User } from '../types';

/**
 * Auth Context
 * Manages user authentication state using Firebase Authentication
 */

interface AuthState {
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  refreshUser: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  console.log('🔐 AuthProvider rendering...');
  
  const [state, setState] = useState<AuthState>({
    currentUser: null,
    isAuthenticated: false,
    isLoading: false, // START as false - don't block app from opening!
  });

  // Subscribe to Firebase auth state changes (runs in background, doesn't block app)
  useEffect(() => {
    console.log('🔐 Setting up Firebase auth state listener (non-blocking)...');
    
    try {
      const unsubscribe = onAuthStateChange((user) => {
        console.log('🔐 Auth state changed:', user ? `User ${user.username} (${user.id})` : 'No user');
        
        setState({
          currentUser: user,
          isAuthenticated: !!user,
          isLoading: false,
        });
      });

      // Cleanup subscription on unmount
      return () => {
        console.log('🔐 Cleaning up auth state listener');
        unsubscribe();
      };
    } catch (error) {
      console.error('❌ Failed to set up auth listener:', error);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    // Firebase auth state is automatically managed via onAuthStateChange
    // This function is kept for compatibility but doesn't need to do anything
    console.log('refreshUser called - Firebase manages auth state automatically');
  }, []);

  const value: AuthContextValue = {
    ...state,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
